import React from "react";
import { Card, CardContent, Typography, Box, Grid } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { InterviewsConductedData } from "../../types";

interface InterviewsConductedProps {
  data: InterviewsConductedData;
  useVerbiage: "state" | "organization" | "nationwide";
  stateOrOrganization: string;
  isExport?: boolean;
}

const InterviewsConducted: React.FC<InterviewsConductedProps> = ({
  data,
  useVerbiage,
  stateOrOrganization,
  isExport = false,
}) => {
  const getVerbiage = () => {
    let verbiage =
      useVerbiage === "state"
        ? `in ${stateOrOrganization}`
        : useVerbiage === "organization"
        ? `by ${stateOrOrganization}`
        : "nationwide";

    let subtitle =
      useVerbiage === "state"
        ? `Securely recorded forensic interviews conducted each month across reporting agencies ${verbiage}`
        : useVerbiage === "organization"
        ? "Your team securely recorded forensic interviews each month."
        : "Securely recorded forensic interviews conducted each month across all reporting agencies nationwide.";

    let bottomCaption = isExport
      ? "Monthly forensic interviews conducted in the last 12 months"
      : `Monthly forensic interviews conducted ${verbiage} in the last 12 months`;

    return [bottomCaption, subtitle];
  };

  const chartOptions: Highcharts.Options = {
    chart: {
      animation: isExport ? false : true,
      type: "column",
      backgroundColor: "transparent",
      height: isExport ? 190 : 300,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: data.monthlyData.map((item) => {
        const [year, month] = item.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleString("default", { month: "short" });
      }),
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
        },
      },
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
        },
      },
      gridLineColor: "#F1F5F9",
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        borderRadius: 4,
        dataLabels: {
          enabled: false,
        },
        pointWidth: isExport ? 30 : 45,
      },
    },
    series: [
      {
        type: "column",
        name: "Interviews",
        data: data.monthlyData.map((item) =>
          item.interviews >= 0 ? item.interviews : 0
        ),
        color: {
          linearGradient: {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 1,
          },
          stops: [
            [0, "#8A4B9D"],
            [1, "#592b7b"],
          ],
        },
      },
    ],
    credits: {
      enabled: false,
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.x}</b><br/>Interviews: ${this.y?.toLocaleString()}`;
      },
    },
  };

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{
          p: isExport ? 0 : 3,
          pb: isExport ? 0 : 3,
          height: isExport ? "100%" : "auto",
        }}
      >
        <Typography
          variant={isExport ? "body1" : "h5"}
          component="h2"
          sx={{
            mb: 1,
            fontWeight: 600,
            textAlign: isExport ? "center" : "left",
          }}
        >
          Number of Interviews Conducted
        </Typography>
        <Typography
          variant={isExport ? "body2" : "body1"}
          color="text.secondary"
          sx={{
            display: isExport ? "none" : "block",
            mb: 3,
          }}
        >
          {getVerbiage()[1]}
        </Typography>

        <Grid
          container
          spacing={isExport ? 0 : 3}
          sx={{ mb: isExport ? 1 : 3 }}
        >
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant={isExport ? "h5" : "h4"}
                component="div"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 0.5,
                }}
              >
                {data.totalInterviews.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: isExport ? "0.5rem" : "0.75rem" }}
              >
                Total Interviews (Last 12 Months)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant={isExport ? "h5" : "h4"}
                component="div"
                sx={{
                  fontWeight: 700,
                  color: "#8A4B9D",
                  mb: 0.5,
                }}
              >
                {data.cumulativeInterviews.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: isExport ? "0.5rem" : "0.75rem" }}
              >
                Cumulative Total Interviews Logged
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: isExport ? 0 : 2, textAlign: "center" }}
        >
          {getVerbiage()[0]}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default InterviewsConducted;
